/**
 * SolarPlanetRenderer.jsx
 *
 * A self-contained Three.js WebGL component that renders a procedural 3D planet
 * using one of 20 vastly different GLSL shaders, all themed around solar energy.
 *
 * Each shader is fully procedural — no image textures required.
 * The component creates its own renderer, scene, camera, and geometry.
 */

import { useEffect, useRef } from "react";
import * as THREE from "three";

/* ------------------------------------------------------------------ */
/*  Shared GLSL helpers injected into every fragment shader            */
/* ------------------------------------------------------------------ */
const GLSL_COMMON = /* glsl */ `
precision highp float;

uniform float uTime;
uniform vec2  uResolution;
uniform vec3  uColor;
uniform vec3  uAccent;
uniform vec3  uLightDir;

varying vec2 vUv;
varying vec3 vNormal;
varying vec3 vPosition;
varying vec3 vWorldPos;

// --- noise helpers ---------------------------------------------------
float hash(float n) { return fract(sin(n) * 43758.5453); }
float hash21(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }

vec2 hash22(vec2 p) {
  p = vec2(dot(p, vec2(127.1, 311.7)), dot(p, vec2(269.5, 183.3)));
  return fract(sin(p) * 43758.5453);
}

float noise(vec3 x) {
  vec3 p = floor(x);
  vec3 f = fract(x);
  f = f * f * (3.0 - 2.0 * f);
  float n = p.x + p.y * 157.0 + 113.0 * p.z;
  return mix(
    mix(mix(hash(n + 0.0), hash(n + 1.0), f.x),
        mix(hash(n + 157.0), hash(n + 158.0), f.x), f.y),
    mix(mix(hash(n + 113.0), hash(n + 114.0), f.x),
        mix(hash(n + 270.0), hash(n + 271.0), f.x), f.y),
    f.z);
}

float noise2D(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  f = f * f * (3.0 - 2.0 * f);
  float a = hash21(i);
  float b = hash21(i + vec2(1.0, 0.0));
  float c = hash21(i + vec2(0.0, 1.0));
  float d = hash21(i + vec2(1.0, 1.0));
  return mix(mix(a, b, f.x), mix(c, d, f.x), f.y);
}

float fbm(vec3 p) {
  float v = 0.0;
  float a = 0.5;
  vec3 shift = vec3(100.0);
  for (int i = 0; i < 5; i++) {
    v += a * noise(p);
    p = p * 2.0 + shift;
    a *= 0.5;
  }
  return v;
}

float fbm2(vec2 p) {
  float v = 0.0;
  float a = 0.5;
  for (int i = 0; i < 5; i++) {
    v += a * noise2D(p);
    p = p * 2.0 + vec2(100.0);
    a *= 0.5;
  }
  return v;
}

// Voronoi
float voronoi(vec2 p) {
  vec2 n = floor(p);
  vec2 f = fract(p);
  float md = 8.0;
  for (int j = -1; j <= 1; j++) {
    for (int i = -1; i <= 1; i++) {
      vec2 g = vec2(float(i), float(j));
      vec2 o = hash22(n + g);
      o = 0.5 + 0.5 * sin(uTime * 0.5 + 6.2831 * o);
      vec2 r = g + o - f;
      float d = dot(r, r);
      md = min(md, d);
    }
  }
  return sqrt(md);
}
`;

/* ------------------------------------------------------------------ */
/*  Shared vertex shader                                               */
/* ------------------------------------------------------------------ */
const VERTEX_SHADER = /* glsl */ `
precision highp float;

uniform float uTime;

varying vec2 vUv;
varying vec3 vNormal;
varying vec3 vPosition;
varying vec3 vWorldPos;

void main() {
  vUv = uv;
  vNormal = normalize(normalMatrix * normal);
  vPosition = position;
  vWorldPos = (modelMatrix * vec4(position, 1.0)).xyz;
  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}
`;

/* ------------------------------------------------------------------ */
/*  20 unique fragment shaders                                         */
/* ------------------------------------------------------------------ */

// 1. SOLAR PANEL — hexagonal photovoltaic grid with reflective cells
const FS_SOLAR_PANEL = /* glsl */ `
${GLSL_COMMON}

void main() {
  vec3 N = normalize(vNormal);
  vec3 L = normalize(uLightDir);
  float ndl = max(dot(N, L), 0.0);

  // Spherical UV
  float theta = atan(vPosition.z, vPosition.x);
  float phi = asin(clamp(vPosition.y, -1.0, 1.0));
  vec2 sUv = vec2(theta / 6.2831 + 0.5, phi / 3.1416 + 0.5);

  // Hex grid
  vec2 p = sUv * 18.0;
  vec2 h = vec2(1.0, 1.732);
  vec2 a = mod(p, h) - h * 0.5;
  vec2 b = mod(p - h * 0.5, h) - h * 0.5;
  vec2 gv = dot(a, a) < dot(b, b) ? a : b;
  float hexDist = max(abs(gv.x), abs(gv.y * 0.577 + abs(gv.x) * 0.5));
  float hexEdge = smoothstep(0.42, 0.44, hexDist);

  // Cell color varies slightly
  float cellVar = hash21(floor(p));
  vec3 cellColor = mix(uColor, uAccent, cellVar * 0.3);

  // Specular reflection per cell
  vec3 V = normalize(vec3(0.0, 0.0, 2.0) - vWorldPos);
  vec3 H = normalize(L + V);
  float spec = pow(max(dot(N, H), 0.0), 80.0);

  // Cell grid lines glow
  float gridGlow = (1.0 - hexEdge) * 0.08;

  vec3 col = cellColor * (0.3 + ndl * 0.7);
  col += uAccent * spec * 0.6;
  col = mix(col, uAccent * 0.3, hexEdge * 0.7);
  col += uAccent * gridGlow;

  // Ambient pulse
  col += uAccent * 0.04 * sin(uTime * 2.0 + sUv.x * 20.0);

  // Atmosphere rim
  float rim = pow(1.0 - max(dot(N, normalize(vec3(0.0, 0.0, 1.0))), 0.0), 3.0);
  col += uAccent * rim * 0.3;

  gl_FragColor = vec4(col, 1.0);
}
`;

// 2. SOLAR CAR — aerodynamic flow lines with energy veins
const FS_SOLAR_CAR = /* glsl */ `
${GLSL_COMMON}

void main() {
  vec3 N = normalize(vNormal);
  vec3 L = normalize(uLightDir);
  float ndl = max(dot(N, L), 0.0);

  float theta = atan(vPosition.z, vPosition.x);
  float phi = asin(clamp(vPosition.y, -1.0, 1.0));
  vec2 sUv = vec2(theta / 6.2831, phi / 3.1416 + 0.5);

  // Flowing aerodynamic lines
  float flow = sin(sUv.x * 40.0 + sUv.y * 8.0 + uTime * 1.5) * 0.5 + 0.5;
  flow = smoothstep(0.4, 0.6, flow);

  // Energy veins pulsing
  float veins = abs(sin(sUv.x * 60.0 + sin(sUv.y * 12.0) * 3.0 + uTime * 3.0));
  veins = pow(veins, 8.0);

  // Speed lines (horizontal streaks)
  float speed = noise(vec3(sUv * 30.0, uTime * 0.8));
  speed = smoothstep(0.65, 0.7, speed) * 0.4;

  vec3 base = mix(uColor, uColor * 1.3, flow * 0.5);
  base *= (0.35 + ndl * 0.65);

  // Add energy veins
  base += uAccent * veins * 0.8;
  base += uAccent * speed;

  // Metallic specular
  vec3 V = normalize(-vWorldPos);
  vec3 R = reflect(-L, N);
  float spec = pow(max(dot(V, R), 0.0), 60.0);
  base += vec3(1.0) * spec * 0.5;

  float rim = pow(1.0 - max(dot(N, normalize(vec3(0.0, 0.0, 1.0))), 0.0), 2.5);
  base += uAccent * rim * 0.25;

  gl_FragColor = vec4(base, 1.0);
}
`;

// 3. SOLAR FARM — golden desert with rows of mirror panels
const FS_SOLAR_FARM = /* glsl */ `
${GLSL_COMMON}

void main() {
  vec3 N = normalize(vNormal);
  vec3 L = normalize(uLightDir);
  float ndl = max(dot(N, L), 0.0);

  float theta = atan(vPosition.z, vPosition.x);
  float phi = asin(clamp(vPosition.y, -1.0, 1.0));
  vec2 sUv = vec2(theta / 6.2831 + 0.5, phi / 3.1416 + 0.5);

  // Desert sand base with dune noise
  float dunes = fbm2(sUv * 8.0 + uTime * 0.02);
  vec3 sand = mix(uColor, uColor * 1.4, dunes);

  // Rows of solar panels (latitude bands)
  float rows = abs(sin(sUv.y * 60.0 + sin(sUv.x * 4.0) * 0.5));
  rows = smoothstep(0.85, 0.9, rows);

  // Panel reflection flicker
  float flicker = sin(uTime * 4.0 + sUv.x * 30.0) * 0.5 + 0.5;
  flicker = smoothstep(0.7, 0.8, flicker);

  vec3 panelColor = vec3(0.15, 0.2, 0.35) + uAccent * flicker * 0.3;

  vec3 col = mix(sand, panelColor, rows);
  col *= (0.4 + ndl * 0.6);

  // Hot shimmer effect
  float shimmer = sin(sUv.y * 100.0 + uTime * 5.0) * 0.02;
  col += uAccent * abs(shimmer);

  // Sun glare
  float sunDot = max(dot(N, L), 0.0);
  col += uAccent * pow(sunDot, 20.0) * 0.4;

  float rim = pow(1.0 - max(dot(N, normalize(vec3(0.0, 0.0, 1.0))), 0.0), 3.0);
  col += uAccent * rim * 0.15;

  gl_FragColor = vec4(col, 1.0);
}
`;

// 4. SOLAR CELL — crystalline p-n junction lattice
const FS_SOLAR_CELL = /* glsl */ `
${GLSL_COMMON}

void main() {
  vec3 N = normalize(vNormal);
  vec3 L = normalize(uLightDir);
  float ndl = max(dot(N, L), 0.0);

  float theta = atan(vPosition.z, vPosition.x);
  float phi = asin(clamp(vPosition.y, -1.0, 1.0));
  vec2 sUv = vec2(theta / 6.2831 + 0.5, phi / 3.1416 + 0.5);

  // Crystal lattice grid
  vec2 lattice = fract(sUv * 20.0) - 0.5;
  float latticeDist = length(lattice);
  float latticePattern = smoothstep(0.05, 0.08, latticeDist);

  // Electron flow particles
  float electrons = sin(sUv.x * 80.0 + uTime * 6.0) * sin(sUv.y * 80.0 - uTime * 4.0);
  electrons = smoothstep(0.92, 1.0, electrons);

  // P-N junction boundary
  float junction = smoothstep(-0.02, 0.02, sin(sUv.y * 3.1416 * 4.0));

  vec3 pSide = uColor * 0.8;
  vec3 nSide = uColor * 1.3;
  vec3 base = mix(pSide, nSide, junction);
  base *= latticePattern;
  base *= (0.3 + ndl * 0.7);

  // Lattice nodes glow
  base += uAccent * (1.0 - latticePattern) * 0.6;

  // Electron particles
  base += uAccent * electrons * 1.2;

  // Band gap emission
  float bandGap = sin(sUv.y * 40.0 + uTime * 2.0) * 0.5 + 0.5;
  bandGap = pow(bandGap, 12.0);
  base += uAccent * bandGap * 0.3;

  float rim = pow(1.0 - max(dot(N, normalize(vec3(0.0, 0.0, 1.0))), 0.0), 3.5);
  base += uAccent * rim * 0.4;

  gl_FragColor = vec4(base, 1.0);
}
`;

// 5. SOLAR THERMAL — molten salt rivers, concentrated heat
const FS_SOLAR_THERMAL = /* glsl */ `
${GLSL_COMMON}

void main() {
  vec3 N = normalize(vNormal);
  vec3 L = normalize(uLightDir);
  float ndl = max(dot(N, L), 0.0);

  float theta = atan(vPosition.z, vPosition.x);
  float phi = asin(clamp(vPosition.y, -1.0, 1.0));
  vec2 sUv = vec2(theta / 6.2831 + 0.5, phi / 3.1416 + 0.5);

  // Volcanic/molten surface
  float lava = fbm(vec3(sUv * 6.0, uTime * 0.2));
  float cracks = fbm(vec3(sUv * 12.0, uTime * 0.1 + 50.0));
  cracks = smoothstep(0.35, 0.45, cracks);

  // Molten rivers
  float rivers = noise(vec3(sUv.x * 8.0 + uTime * 0.3, sUv.y * 4.0, uTime * 0.15));
  rivers = smoothstep(0.55, 0.62, rivers);

  vec3 rock = uColor * (0.4 + lava * 0.4);
  vec3 molten = uAccent * 1.5 + vec3(1.0, 0.4, 0.0) * 0.5;

  vec3 col = mix(rock, molten, rivers);
  col = mix(col, molten * 0.6, cracks * 0.3);
  col *= (0.3 + ndl * 0.7);

  // Heat shimmer
  col += vec3(1.0, 0.5, 0.1) * pow(ndl, 4.0) * 0.3;

  // Concentrated tower glow (at north pole)
  float towerGlow = smoothstep(0.8, 1.0, sUv.y);
  col += vec3(1.0, 0.9, 0.5) * towerGlow * (0.5 + 0.3 * sin(uTime * 3.0));

  float rim = pow(1.0 - max(dot(N, normalize(vec3(0.0, 0.0, 1.0))), 0.0), 2.0);
  col += uAccent * rim * 0.35;

  gl_FragColor = vec4(col, 1.0);
}
`;

// 6. SOLAR SATELLITE — orbital structure with solar sail arrays
const FS_SOLAR_SATELLITE = /* glsl */ `
${GLSL_COMMON}

void main() {
  vec3 N = normalize(vNormal);
  vec3 L = normalize(uLightDir);
  float ndl = max(dot(N, L), 0.0);

  float theta = atan(vPosition.z, vPosition.x);
  float phi = asin(clamp(vPosition.y, -1.0, 1.0));
  vec2 sUv = vec2(theta / 6.2831 + 0.5, phi / 3.1416 + 0.5);

  // Space hull (dark metallic)
  vec3 hull = uColor * 0.5;

  // Solar sail grid (large rectangular panels)
  vec2 sailGrid = fract(sUv * vec2(6.0, 3.0));
  float sailEdge = step(0.05, sailGrid.x) * step(sailGrid.x, 0.95) *
                   step(0.05, sailGrid.y) * step(sailGrid.y, 0.95);

  // Sail surface shimmer
  float sailShimmer = sin(sUv.x * 60.0 + uTime * 2.0) * 0.5 + 0.5;
  vec3 sailColor = mix(uAccent * 0.5, uAccent, sailShimmer * 0.5);

  // Orbit rings
  float rings = abs(sin(phi * 20.0 + uTime * 0.5));
  rings = smoothstep(0.95, 1.0, rings);

  vec3 col = mix(hull, sailColor, sailEdge * 0.8);
  col *= (0.2 + ndl * 0.8);

  // Orbit ring glow
  col += uAccent * rings * 0.15;

  // Microwave beam (equatorial band)
  float beam = smoothstep(0.48, 0.5, sUv.y) * smoothstep(0.52, 0.5, sUv.y);
  beam *= (0.5 + 0.5 * sin(uTime * 8.0 + sUv.x * 40.0));
  col += uAccent * beam * 0.6;

  // Star-like specular
  vec3 V = normalize(-vWorldPos);
  vec3 R = reflect(-L, N);
  float spec = pow(max(dot(V, R), 0.0), 120.0);
  col += vec3(1.0) * spec * 0.8;

  float rim = pow(1.0 - max(dot(N, normalize(vec3(0.0, 0.0, 1.0))), 0.0), 4.0);
  col += uAccent * rim * 0.2;

  gl_FragColor = vec4(col, 1.0);
}
`;

// 7. SOLAR AI — neural network nodes with data flow
const FS_SOLAR_AI = /* glsl */ `
${GLSL_COMMON}

void main() {
  vec3 N = normalize(vNormal);
  vec3 L = normalize(uLightDir);
  float ndl = max(dot(N, L), 0.0);

  float theta = atan(vPosition.z, vPosition.x);
  float phi = asin(clamp(vPosition.y, -1.0, 1.0));
  vec2 sUv = vec2(theta / 6.2831 + 0.5, phi / 3.1416 + 0.5);

  // Voronoi neural nodes
  float v = voronoi(sUv * 12.0);
  float nodeGlow = smoothstep(0.12, 0.0, v);
  float edgeGlow = smoothstep(0.35, 0.15, v) - nodeGlow;

  // Data flow through connections
  float dataFlow = sin(v * 20.0 - uTime * 4.0);
  dataFlow = smoothstep(0.8, 1.0, dataFlow) * edgeGlow;

  // Background digital matrix
  float matrix = noise(vec3(sUv * 40.0, uTime * 0.5));
  matrix = step(0.82, matrix) * 0.15;

  vec3 bg = uColor * 0.15;
  vec3 col = bg;

  // Connection edges
  col += uAccent * 0.2 * edgeGlow;

  // Data pulses
  col += uAccent * dataFlow * 0.8;

  // Neural nodes
  col += uAccent * nodeGlow * 1.2;

  // Matrix rain
  col += uAccent * matrix;

  col *= (0.5 + ndl * 0.5);

  // Processing pulse
  float pulse = sin(uTime * 1.5) * 0.5 + 0.5;
  col += uAccent * 0.05 * pulse;

  float rim = pow(1.0 - max(dot(N, normalize(vec3(0.0, 0.0, 1.0))), 0.0), 3.0);
  col += uAccent * rim * 0.3;

  gl_FragColor = vec4(col, 1.0);
}
`;

// 8. SOLAR BATTERY — pulsing ion flow through crystal layers
const FS_SOLAR_BATTERY = /* glsl */ `
${GLSL_COMMON}

void main() {
  vec3 N = normalize(vNormal);
  vec3 L = normalize(uLightDir);
  float ndl = max(dot(N, L), 0.0);

  float theta = atan(vPosition.z, vPosition.x);
  float phi = asin(clamp(vPosition.y, -1.0, 1.0));
  vec2 sUv = vec2(theta / 6.2831 + 0.5, phi / 3.1416 + 0.5);

  // Layered cathode structure (horizontal bands)
  float layers = abs(sin(sUv.y * 30.0));
  layers = smoothstep(0.8, 0.85, layers);

  // Ion migration (particles moving vertically)
  float ions = sin(sUv.x * 50.0 + uTime * 0.5) * sin(sUv.y * 80.0 - uTime * 3.0);
  ions = smoothstep(0.94, 1.0, ions);

  // Charge level gradient (bottom = discharged, top = charged)
  float charge = sUv.y;
  charge = charge + 0.1 * sin(uTime * 0.5);

  vec3 discharged = uColor * 0.4;
  vec3 charged = uAccent;
  vec3 base = mix(discharged, charged, clamp(charge, 0.0, 1.0));

  // Layer separators
  base = mix(base, base * 0.3, layers);

  base *= (0.3 + ndl * 0.7);

  // Ion particles
  base += uAccent * ions * 0.9;

  // Charge pulse wave
  float wave = sin(sUv.y * 10.0 - uTime * 2.0) * 0.5 + 0.5;
  wave = pow(wave, 8.0) * 0.15;
  base += uAccent * wave;

  float rim = pow(1.0 - max(dot(N, normalize(vec3(0.0, 0.0, 1.0))), 0.0), 2.5);
  base += uAccent * rim * 0.25;

  gl_FragColor = vec4(base, 1.0);
}
`;

// 9. SOLAR BLOCKCHAIN — decentralized glowing chain links
const FS_SOLAR_BLOCKCHAIN = /* glsl */ `
${GLSL_COMMON}

void main() {
  vec3 N = normalize(vNormal);
  vec3 L = normalize(uLightDir);
  float ndl = max(dot(N, L), 0.0);

  float theta = atan(vPosition.z, vPosition.x);
  float phi = asin(clamp(vPosition.y, -1.0, 1.0));
  vec2 sUv = vec2(theta / 6.2831 + 0.5, phi / 3.1416 + 0.5);

  // Block grid (rectangular cells)
  vec2 blockUv = fract(sUv * vec2(10.0, 8.0));
  vec2 blockId = floor(sUv * vec2(10.0, 8.0));
  float blockEdge = step(0.06, blockUv.x) * step(blockUv.x, 0.94) *
                    step(0.06, blockUv.y) * step(blockUv.y, 0.94);

  // Chain connections (lines between blocks)
  float chainH = smoothstep(0.02, 0.0, abs(blockUv.y - 0.5)) * (1.0 - blockEdge);
  float chainV = smoothstep(0.02, 0.0, abs(blockUv.x - 0.5)) * (1.0 - blockEdge);

  // Hash animation per block
  float blockHash = hash21(blockId);
  float blockPulse = sin(uTime * 2.0 + blockHash * 6.28) * 0.5 + 0.5;

  // Validation wave propagating across surface
  float validateWave = sin(sUv.x * 6.28 + sUv.y * 3.14 - uTime * 1.5);
  validateWave = smoothstep(0.9, 1.0, validateWave);

  vec3 col = uColor * 0.12;

  // Block fill
  col += uAccent * 0.15 * blockEdge;
  col += uAccent * blockPulse * blockEdge * 0.2;

  // Chain links
  col += uAccent * (chainH + chainV) * 0.5;

  // Validation flash
  col += vec3(1.0) * validateWave * blockEdge * 0.4;

  col *= (0.5 + ndl * 0.5);

  float rim = pow(1.0 - max(dot(N, normalize(vec3(0.0, 0.0, 1.0))), 0.0), 3.0);
  col += uAccent * rim * 0.35;

  gl_FragColor = vec4(col, 1.0);
}
`;

// 10. SOLAR DESALINATION — water/crystal membrane effect
const FS_SOLAR_DESALINATION = /* glsl */ `
${GLSL_COMMON}

void main() {
  vec3 N = normalize(vNormal);
  vec3 L = normalize(uLightDir);
  float ndl = max(dot(N, L), 0.0);

  float theta = atan(vPosition.z, vPosition.x);
  float phi = asin(clamp(vPosition.y, -1.0, 1.0));
  vec2 sUv = vec2(theta / 6.2831 + 0.5, phi / 3.1416 + 0.5);

  // Ocean base with caustics
  float caustic1 = sin(sUv.x * 30.0 + uTime) * sin(sUv.y * 25.0 + uTime * 0.7);
  float caustic2 = sin(sUv.x * 22.0 - uTime * 0.8) * sin(sUv.y * 28.0 + uTime * 1.1);
  float caustics = smoothstep(0.7, 1.0, (caustic1 + caustic2) * 0.5 + 0.5) * 0.3;

  // Membrane fractal structure
  float membrane = voronoi(sUv * 15.0 + vec2(0.0, uTime * 0.1));
  float membraneEdge = smoothstep(0.08, 0.12, membrane);

  // Evaporation particles rising
  float evap = sin(sUv.x * 40.0) * cos(sUv.y * 20.0 + uTime * 2.0);
  evap = smoothstep(0.95, 1.0, evap) * step(0.5, sUv.y);

  // Water-to-fresh gradient
  vec3 salt = uColor;
  vec3 fresh = uAccent;
  float gradient = smoothstep(0.3, 0.7, sUv.y + fbm2(sUv * 4.0) * 0.15);
  vec3 base = mix(salt, fresh, gradient);

  base *= membraneEdge;
  base += uAccent * (1.0 - membraneEdge) * 0.2;
  base += uAccent * caustics;
  base *= (0.3 + ndl * 0.7);

  // Evaporation glow
  base += vec3(1.0) * evap * 0.4;

  float rim = pow(1.0 - max(dot(N, normalize(vec3(0.0, 0.0, 1.0))), 0.0), 2.5);
  base += uAccent * rim * 0.3;

  gl_FragColor = vec4(base, 1.0);
}
`;

// 11. SOLAR GREENHOUSE — organic leaf veins + transparent panels
const FS_SOLAR_GREENHOUSE = /* glsl */ `
${GLSL_COMMON}

void main() {
  vec3 N = normalize(vNormal);
  vec3 L = normalize(uLightDir);
  float ndl = max(dot(N, L), 0.0);

  float theta = atan(vPosition.z, vPosition.x);
  float phi = asin(clamp(vPosition.y, -1.0, 1.0));
  vec2 sUv = vec2(theta / 6.2831 + 0.5, phi / 3.1416 + 0.5);

  // Leaf-like veins (fractal branching)
  float vein1 = abs(sin(sUv.x * 20.0 + sin(sUv.y * 8.0) * 2.0));
  float vein2 = abs(sin(sUv.y * 15.0 + sin(sUv.x * 6.0) * 3.0));
  float veins = min(vein1, vein2);
  veins = smoothstep(0.0, 0.15, veins);

  // Chloroplast cells (voronoi)
  float cells = voronoi(sUv * 10.0 + vec2(uTime * 0.05, 0.0));
  float cellWall = smoothstep(0.08, 0.15, cells);

  // Photosynthesis glow
  float photo = noise(vec3(sUv * 20.0, uTime * 0.3));
  photo = smoothstep(0.6, 0.8, photo) * 0.3;

  // Dome glass panels (subtle grid overlay)
  vec2 glass = fract(sUv * vec2(8.0, 5.0));
  float glassEdge = step(0.03, glass.x) * step(0.03, glass.y);

  vec3 leafGreen = uColor;
  vec3 lightGreen = uAccent;
  vec3 base = mix(lightGreen * 0.8, leafGreen, cellWall);
  base = mix(base, leafGreen * 0.5, 1.0 - veins);
  base += lightGreen * photo;
  base *= glassEdge * 0.8 + 0.2;
  base *= (0.3 + ndl * 0.7);

  // Subsurface scattering (light through leaves)
  float sss = pow(max(dot(-N, L), 0.0), 2.0) * 0.3;
  base += lightGreen * sss;

  float rim = pow(1.0 - max(dot(N, normalize(vec3(0.0, 0.0, 1.0))), 0.0), 2.5);
  base += uAccent * rim * 0.2;

  gl_FragColor = vec4(base, 1.0);
}
`;

// 12. SOLAR PEROVSKITE — iridescent crystal lattice with ABX3 structure
const FS_SOLAR_PEROVSKITE = /* glsl */ `
${GLSL_COMMON}

void main() {
  vec3 N = normalize(vNormal);
  vec3 L = normalize(uLightDir);
  float ndl = max(dot(N, L), 0.0);

  float theta = atan(vPosition.z, vPosition.x);
  float phi = asin(clamp(vPosition.y, -1.0, 1.0));
  vec2 sUv = vec2(theta / 6.2831 + 0.5, phi / 3.1416 + 0.5);

  // ABX3 crystal lattice (cubic grid with atom sites)
  vec2 latticeA = fract(sUv * 12.0) - 0.5;
  vec2 latticeB = fract(sUv * 12.0 + 0.5) - 0.5;
  float atomA = smoothstep(0.12, 0.08, length(latticeA));
  float atomB = smoothstep(0.08, 0.05, length(latticeB));

  // Iridescent color shift based on viewing angle
  float viewAngle = dot(N, normalize(vec3(0.0, 0.0, 1.0)));
  vec3 iridescence = vec3(
    sin(viewAngle * 6.28 + 0.0) * 0.5 + 0.5,
    sin(viewAngle * 6.28 + 2.09) * 0.5 + 0.5,
    sin(viewAngle * 6.28 + 4.19) * 0.5 + 0.5
  );

  // Crystal facets
  float facet = noise(vec3(sUv * 25.0, uTime * 0.1));
  facet = smoothstep(0.4, 0.6, facet);

  vec3 base = mix(uColor, uAccent, facet * 0.4);
  base = mix(base, iridescence, 0.3);

  // Crystal atoms
  base += uAccent * atomA * 0.8;
  base += vec3(1.0, 0.8, 0.5) * atomB * 0.6;

  base *= (0.25 + ndl * 0.75);

  // Quantum efficiency sparkle
  float sparkle = hash21(floor(sUv * 60.0 + uTime * 0.5));
  sparkle = step(0.97, sparkle);
  base += vec3(1.0) * sparkle * 0.6;

  float rim = pow(1.0 - max(dot(N, normalize(vec3(0.0, 0.0, 1.0))), 0.0), 3.0);
  base += uAccent * rim * 0.35;

  gl_FragColor = vec4(base, 1.0);
}
`;

// 13. SOLAR DRONE — wireframe structural skin with propeller turbulence
const FS_SOLAR_DRONE = /* glsl */ `
${GLSL_COMMON}

void main() {
  vec3 N = normalize(vNormal);
  vec3 L = normalize(uLightDir);
  float ndl = max(dot(N, L), 0.0);

  float theta = atan(vPosition.z, vPosition.x);
  float phi = asin(clamp(vPosition.y, -1.0, 1.0));
  vec2 sUv = vec2(theta / 6.2831 + 0.5, phi / 3.1416 + 0.5);

  // Wireframe carbon fiber structure
  vec2 wireUv = fract(sUv * 30.0);
  float wireX = smoothstep(0.02, 0.0, abs(wireUv.x - 0.5));
  float wireY = smoothstep(0.02, 0.0, abs(wireUv.y - 0.5));
  float wire = max(wireX, wireY);

  // Carbon fiber weave
  float weave = sin(sUv.x * 60.0 + sUv.y * 60.0) * sin(sUv.x * 60.0 - sUv.y * 60.0);
  weave = smoothstep(0.6, 0.7, weave) * 0.15;

  // Propeller blur zones (4 spots)
  float props = 0.0;
  for (int i = 0; i < 4; i++) {
    float a = float(i) * 1.5708;
    vec2 propCenter = vec2(0.5 + cos(a) * 0.25, 0.5 + sin(a) * 0.15);
    float d = length(sUv - propCenter);
    float spin = sin(theta * 8.0 + uTime * 20.0 + float(i) * 1.57);
    props += smoothstep(0.08, 0.04, d) * abs(spin);
  }

  vec3 base = uColor * 0.3;
  base += uAccent * wire * 0.5;
  base += vec3(0.15) * weave;
  base *= (0.3 + ndl * 0.7);

  // Propeller motion blur
  base += uAccent * props * 0.3;

  // Navigation lights
  float navLight = step(0.98, sin(theta * 4.0 + uTime * 6.0)) * step(0.8, sUv.y);
  base += vec3(1.0, 0.2, 0.2) * navLight * 0.5;

  // Metallic specular
  vec3 V = normalize(-vWorldPos);
  vec3 R = reflect(-L, N);
  float spec = pow(max(dot(V, R), 0.0), 40.0);
  base += uAccent * spec * 0.3;

  float rim = pow(1.0 - max(dot(N, normalize(vec3(0.0, 0.0, 1.0))), 0.0), 3.0);
  base += uAccent * rim * 0.15;

  gl_FragColor = vec4(base, 1.0);
}
`;

// 14. SOLAR PAINT — quantum dot particles with organic paint swirls
const FS_SOLAR_PAINT = /* glsl */ `
${GLSL_COMMON}

void main() {
  vec3 N = normalize(vNormal);
  vec3 L = normalize(uLightDir);
  float ndl = max(dot(N, L), 0.0);

  float theta = atan(vPosition.z, vPosition.x);
  float phi = asin(clamp(vPosition.y, -1.0, 1.0));
  vec2 sUv = vec2(theta / 6.2831 + 0.5, phi / 3.1416 + 0.5);

  // Paint swirl (distorted FBM)
  vec2 distort = vec2(
    fbm2(sUv * 3.0 + uTime * 0.05),
    fbm2(sUv * 3.0 + vec2(5.0) + uTime * 0.04)
  );
  float swirl = fbm2(sUv * 4.0 + distort * 1.5);

  // Quantum dots (tiny bright particles)
  float dots = 0.0;
  for (int i = 0; i < 3; i++) {
    vec2 dotUv = sUv * (30.0 + float(i) * 15.0) + float(i) * 100.0;
    float d = hash21(floor(dotUv));
    dots += step(0.96, d) * (1.0 - fract(d + uTime * 0.3));
  }

  // Paint brush strokes
  float brush = noise(vec3(sUv.x * 2.0, sUv.y * 40.0, uTime * 0.1));
  brush = smoothstep(0.45, 0.55, brush) * 0.15;

  // Multi-color paint base
  vec3 paint1 = uColor;
  vec3 paint2 = uAccent;
  vec3 paint3 = mix(uColor, uAccent, 0.5) * 1.3;
  vec3 base = mix(paint1, paint2, swirl);
  base = mix(base, paint3, smoothstep(0.4, 0.6, swirl) * 0.4);
  base += vec3(0.1) * brush;
  base *= (0.35 + ndl * 0.65);

  // Quantum dot sparkle
  base += uAccent * dots * 0.8;
  base += vec3(1.0, 0.9, 0.6) * dots * 0.3;

  // Wet paint gloss
  vec3 V = normalize(-vWorldPos);
  vec3 R = reflect(-L, N);
  float gloss = pow(max(dot(V, R), 0.0), 30.0);
  base += vec3(1.0) * gloss * 0.25;

  float rim = pow(1.0 - max(dot(N, normalize(vec3(0.0, 0.0, 1.0))), 0.0), 2.0);
  base += uAccent * rim * 0.2;

  gl_FragColor = vec4(base, 1.0);
}
`;

// 15. SOLAR GRID — pulsing network mesh with power flow
const FS_SOLAR_GRID = /* glsl */ `
${GLSL_COMMON}

void main() {
  vec3 N = normalize(vNormal);
  vec3 L = normalize(uLightDir);
  float ndl = max(dot(N, L), 0.0);

  float theta = atan(vPosition.z, vPosition.x);
  float phi = asin(clamp(vPosition.y, -1.0, 1.0));
  vec2 sUv = vec2(theta / 6.2831 + 0.5, phi / 3.1416 + 0.5);

  // Power grid network
  vec2 gridUv = fract(sUv * 16.0);
  vec2 gridId = floor(sUv * 16.0);
  float gridLineX = smoothstep(0.03, 0.0, abs(gridUv.x - 0.5));
  float gridLineY = smoothstep(0.03, 0.0, abs(gridUv.y - 0.5));
  float gridLines = max(gridLineX, gridLineY);

  // Node points at intersections
  float node = smoothstep(0.15, 0.08, length(gridUv - 0.5));

  // Power flow animation along lines
  float flowH = sin(sUv.x * 80.0 - uTime * 5.0) * 0.5 + 0.5;
  flowH = pow(flowH, 4.0) * gridLineY;

  float flowV = sin(sUv.y * 80.0 - uTime * 4.0) * 0.5 + 0.5;
  flowV = pow(flowV, 4.0) * gridLineX;

  // Load visualization (each cell brightness)
  float load = hash21(gridId) * 0.5 + 0.25;
  load += sin(uTime + hash21(gridId) * 6.28) * 0.15;

  vec3 col = uColor * 0.08;
  col += uAccent * gridLines * 0.2;
  col += uAccent * node * 0.8;
  col += uAccent * (flowH + flowV) * 0.6;

  // Cell load glow
  float cellGlow = (1.0 - gridLines) * (1.0 - node) * load;
  col += uColor * cellGlow * 0.3;

  col *= (0.5 + ndl * 0.5);

  // Overload warning pulse
  float overload = step(0.85, load) * (sin(uTime * 8.0) * 0.5 + 0.5);
  col += vec3(1.0, 0.3, 0.1) * overload * node * 0.5;

  float rim = pow(1.0 - max(dot(N, normalize(vec3(0.0, 0.0, 1.0))), 0.0), 3.0);
  col += uAccent * rim * 0.25;

  gl_FragColor = vec4(col, 1.0);
}
`;

// 16. SOLAR ROADWAY — hexagonal road tiles with LED patterns
const FS_SOLAR_ROADWAY = /* glsl */ `
${GLSL_COMMON}

void main() {
  vec3 N = normalize(vNormal);
  vec3 L = normalize(uLightDir);
  float ndl = max(dot(N, L), 0.0);

  float theta = atan(vPosition.z, vPosition.x);
  float phi = asin(clamp(vPosition.y, -1.0, 1.0));
  vec2 sUv = vec2(theta / 6.2831 + 0.5, phi / 3.1416 + 0.5);

  // Hexagonal tile pattern
  vec2 p = sUv * 14.0;
  vec2 h = vec2(1.0, 1.732);
  vec2 a = mod(p, h) - h * 0.5;
  vec2 b = mod(p - h * 0.5, h) - h * 0.5;
  vec2 gv = dot(a, a) < dot(b, b) ? a : b;
  float hexDist = max(abs(gv.x), abs(gv.y * 0.577 + abs(gv.x) * 0.5));
  float hexEdge = smoothstep(0.38, 0.42, hexDist);

  // LED dots within each hex
  float ledDist = length(gv);
  float leds = smoothstep(0.1, 0.06, ledDist);

  // Road lane markers
  float lane = abs(sin(sUv.y * 6.28 * 3.0));
  lane = smoothstep(0.96, 1.0, lane) * smoothstep(0.48, 0.5, abs(sUv.x - 0.5));

  // Traffic flow animation
  float traffic = sin(sUv.x * 40.0 + uTime * 3.0 * sign(sUv.y - 0.5));
  traffic = smoothstep(0.9, 1.0, traffic) * 0.3;

  vec3 asphalt = uColor * 0.4;
  vec3 col = asphalt;

  // Hex edges (tempered glass)
  col = mix(col, uColor * 0.6, hexEdge * 0.5);

  // LED indicators
  float ledPulse = sin(uTime * 2.0 + hash21(floor(p)) * 6.28) * 0.5 + 0.5;
  col += uAccent * leds * ledPulse;

  // Lane markers
  col += uAccent * lane * 0.6;

  // Traffic
  col += vec3(1.0, 0.9, 0.7) * traffic;

  col *= (0.3 + ndl * 0.7);

  float rim = pow(1.0 - max(dot(N, normalize(vec3(0.0, 0.0, 1.0))), 0.0), 2.5);
  col += uAccent * rim * 0.15;

  gl_FragColor = vec4(col, 1.0);
}
`;

// 17. SOLAR WINDOW — transparent glass refraction with organic PV film
const FS_SOLAR_WINDOW = /* glsl */ `
${GLSL_COMMON}

void main() {
  vec3 N = normalize(vNormal);
  vec3 L = normalize(uLightDir);
  float ndl = max(dot(N, L), 0.0);

  float theta = atan(vPosition.z, vPosition.x);
  float phi = asin(clamp(vPosition.y, -1.0, 1.0));
  vec2 sUv = vec2(theta / 6.2831 + 0.5, phi / 3.1416 + 0.5);

  // Glass pane grid (building windows)
  vec2 windowGrid = fract(sUv * vec2(8.0, 12.0));
  vec2 windowId = floor(sUv * vec2(8.0, 12.0));
  float windowFrame = step(0.06, windowGrid.x) * step(windowGrid.x, 0.94) *
                      step(0.04, windowGrid.y) * step(windowGrid.y, 0.96);

  // Organic PV film (thin-film interference)
  float filmThickness = noise(vec3(sUv * 15.0, 0.0)) * 2.0;
  vec3 interference = vec3(
    sin(filmThickness * 6.28 + 0.0) * 0.5 + 0.5,
    sin(filmThickness * 6.28 + 2.09) * 0.5 + 0.5,
    sin(filmThickness * 6.28 + 4.19) * 0.5 + 0.5
  );

  // Reflection of clouds/sky in glass
  float clouds = fbm2(sUv * 3.0 + vec2(uTime * 0.02, 0.0));

  // Window frame (metallic)
  vec3 frame = vec3(0.3, 0.35, 0.4);

  // Glass interior (slight tint + PV film)
  vec3 glass = mix(uColor * 0.3 + vec3(0.1, 0.12, 0.2), interference * 0.3, 0.4);
  glass += uAccent * 0.1;
  glass += vec3(0.5, 0.6, 0.8) * clouds * 0.15;

  // Power generation indicator (subtle glow per pane)
  float power = hash21(windowId);
  float powerPulse = sin(uTime * 1.5 + power * 6.28) * 0.5 + 0.5;

  vec3 col = mix(frame, glass, windowFrame);
  col += uAccent * windowFrame * power * powerPulse * 0.15;
  col *= (0.3 + ndl * 0.7);

  // Glass specular
  vec3 V = normalize(-vWorldPos);
  vec3 R = reflect(-L, N);
  float spec = pow(max(dot(V, R), 0.0), 60.0);
  col += vec3(1.0) * spec * windowFrame * 0.4;

  float rim = pow(1.0 - max(dot(N, normalize(vec3(0.0, 0.0, 1.0))), 0.0), 4.0);
  col += uAccent * rim * 0.2;

  gl_FragColor = vec4(col, 1.0);
}
`;

// 18. SOLAR HYDROGEN — water splitting with bubble effects
const FS_SOLAR_HYDROGEN = /* glsl */ `
${GLSL_COMMON}

void main() {
  vec3 N = normalize(vNormal);
  vec3 L = normalize(uLightDir);
  float ndl = max(dot(N, L), 0.0);

  float theta = atan(vPosition.z, vPosition.x);
  float phi = asin(clamp(vPosition.y, -1.0, 1.0));
  vec2 sUv = vec2(theta / 6.2831 + 0.5, phi / 3.1416 + 0.5);

  // Water background with refraction
  float water = fbm2(sUv * 6.0 + uTime * 0.1);

  // H2 bubbles rising
  float bubbles = 0.0;
  for (int i = 0; i < 6; i++) {
    float fi = float(i);
    vec2 bubblePos = vec2(
      0.15 + fi * 0.13 + sin(uTime * 0.5 + fi) * 0.05,
      fract(-uTime * (0.15 + fi * 0.03) + fi * 0.17)
    );
    float size = 0.015 + hash(fi) * 0.02;
    float d = length(sUv - bubblePos);
    bubbles += smoothstep(size, size * 0.5, d);
  }

  // Electrode plates (top and bottom)
  float cathode = smoothstep(0.05, 0.03, sUv.y) + smoothstep(0.95, 0.97, sUv.y);

  // Electrolysis current (vertical waves)
  float current = sin(sUv.x * 30.0 + uTime * 3.0) * sin(sUv.y * 50.0);
  current = smoothstep(0.92, 1.0, current) * 0.3;

  // Catalytic membrane (middle band)
  float membrane = smoothstep(0.48, 0.5, sUv.y) * smoothstep(0.52, 0.5, sUv.y);
  float membranePattern = sin(sUv.x * 80.0) * sin(sUv.y * 200.0);
  membrane *= smoothstep(0.5, 0.8, membranePattern) * 0.5 + 0.5;

  vec3 waterColor = uColor + vec3(0.0, 0.05, 0.1) * water;
  vec3 col = waterColor;

  // Membrane
  col = mix(col, uAccent * 0.6, membrane * 0.4);

  // Electrodes
  col = mix(col, vec3(0.6, 0.65, 0.7), cathode);

  col *= (0.3 + ndl * 0.7);

  // Bubbles (bright, translucent)
  col += uAccent * bubbles * 0.8;
  col += vec3(1.0) * bubbles * 0.3;

  // Current flow
  col += uAccent * current;

  float rim = pow(1.0 - max(dot(N, normalize(vec3(0.0, 0.0, 1.0))), 0.0), 2.5);
  col += uAccent * rim * 0.25;

  gl_FragColor = vec4(col, 1.0);
}
`;

// 19. SOLAR WEARABLE — woven fiber texture with flexible PV threads
const FS_SOLAR_WEARABLE = /* glsl */ `
${GLSL_COMMON}

void main() {
  vec3 N = normalize(vNormal);
  vec3 L = normalize(uLightDir);
  float ndl = max(dot(N, L), 0.0);

  float theta = atan(vPosition.z, vPosition.x);
  float phi = asin(clamp(vPosition.y, -1.0, 1.0));
  vec2 sUv = vec2(theta / 6.2831 + 0.5, phi / 3.1416 + 0.5);

  // Woven fabric texture
  float warpPhase = step(0.5, fract(sUv.y * 40.0));
  float weftPhase = step(0.5, fract(sUv.x * 40.0));
  float weave = abs(warpPhase - weftPhase);

  // Thread detail
  float threadX = sin(sUv.x * 250.0) * 0.5 + 0.5;
  float threadY = sin(sUv.y * 250.0) * 0.5 + 0.5;
  float threadDetail = mix(threadX, threadY, weave) * 0.1;

  // PV fiber highlights (every Nth thread glows)
  float pvFiber = step(0.9, fract(sUv.x * 20.0 + 0.05)) +
                  step(0.9, fract(sUv.y * 20.0 + 0.05));
  pvFiber = min(pvFiber, 1.0);

  // Energy collection pulse
  float energyPulse = sin(uTime * 2.0 + sUv.x * 10.0 + sUv.y * 8.0) * 0.5 + 0.5;

  vec3 fabricBase = uColor * (0.6 + weave * 0.3);
  fabricBase += vec3(threadDetail);
  fabricBase *= (0.35 + ndl * 0.65);

  // PV fiber glow
  fabricBase += uAccent * pvFiber * (0.3 + energyPulse * 0.4);

  // Soft fabric specular (anisotropic-like)
  vec3 V = normalize(-vWorldPos);
  float aniso = abs(dot(normalize(vec3(N.x, 0.0, N.z)), V));
  fabricBase += vec3(1.0) * pow(aniso, 20.0) * 0.12;

  float rim = pow(1.0 - max(dot(N, normalize(vec3(0.0, 0.0, 1.0))), 0.0), 2.0);
  fabricBase += uAccent * rim * 0.15;

  gl_FragColor = vec4(fabricBase, 1.0);
}
`;

// 20. SOLAR DEEP LEARNING — layered neural network with gradient descent
const FS_SOLAR_DEEP_LEARNING = /* glsl */ `
${GLSL_COMMON}

void main() {
  vec3 N = normalize(vNormal);
  vec3 L = normalize(uLightDir);
  float ndl = max(dot(N, L), 0.0);

  float theta = atan(vPosition.z, vPosition.x);
  float phi = asin(clamp(vPosition.y, -1.0, 1.0));
  vec2 sUv = vec2(theta / 6.2831 + 0.5, phi / 3.1416 + 0.5);

  // Neural network layers (vertical columns)
  float layerCount = 7.0;
  float layerX = sUv.x * layerCount;
  float layerIdx = floor(layerX);
  float layerFrac = fract(layerX);

  // Neurons per layer
  float neuronsPerLayer = 4.0 + sin(layerIdx * 1.7) * 2.0;
  float neuronY = sUv.y * neuronsPerLayer;
  float neuronIdx = floor(neuronY);
  float neuronFrac = fract(neuronY);

  // Neuron body
  vec2 neuronCenter = vec2(layerFrac, neuronFrac) - 0.5;
  float neuronDist = length(neuronCenter);
  float neuron = smoothstep(0.25, 0.18, neuronDist);

  // Activation value (changes with time)
  float activation = sin(uTime * 1.5 + layerIdx * 0.8 + neuronIdx * 1.3) * 0.5 + 0.5;

  // Connection lines to next layer
  float connection = smoothstep(0.02, 0.0, abs(neuronCenter.y)) *
                     step(0.5, layerFrac) * (1.0 - neuron);

  // Gradient descent wave (propagating backward)
  float gradientWave = sin((1.0 - sUv.x) * 20.0 - uTime * 3.0) * 0.5 + 0.5;
  gradientWave = pow(gradientWave, 6.0) * 0.3;

  // Loss landscape background
  float loss = fbm2(sUv * 5.0 + vec2(uTime * 0.05));

  vec3 col = uColor * 0.1 + uColor * loss * 0.15;

  // Connections
  col += uAccent * connection * 0.3;

  // Gradient descent
  col += uAccent * gradientWave;

  // Neurons with activation coloring
  vec3 inactiveColor = uColor * 0.5;
  vec3 activeColor = uAccent * 1.2;
  col += mix(inactiveColor, activeColor, activation) * neuron;

  col *= (0.5 + ndl * 0.5);

  // Training progress sparkle
  float sparkle = hash21(floor(sUv * 50.0 + uTime * 0.2));
  sparkle = step(0.98, sparkle);
  col += uAccent * sparkle * 0.4;

  float rim = pow(1.0 - max(dot(N, normalize(vec3(0.0, 0.0, 1.0))), 0.0), 3.0);
  col += uAccent * rim * 0.3;

  gl_FragColor = vec4(col, 1.0);
}
`;

/* ------------------------------------------------------------------ */
/*  Shader map                                                         */
/* ------------------------------------------------------------------ */
const SHADER_MAP = {
  solarPanel: FS_SOLAR_PANEL,
  solarCar: FS_SOLAR_CAR,
  solarFarm: FS_SOLAR_FARM,
  solarCell: FS_SOLAR_CELL,
  solarThermal: FS_SOLAR_THERMAL,
  solarSatellite: FS_SOLAR_SATELLITE,
  solarAI: FS_SOLAR_AI,
  solarBattery: FS_SOLAR_BATTERY,
  solarBlockchain: FS_SOLAR_BLOCKCHAIN,
  solarDesalination: FS_SOLAR_DESALINATION,
  solarGreenhouse: FS_SOLAR_GREENHOUSE,
  solarPerovskite: FS_SOLAR_PEROVSKITE,
  solarDrone: FS_SOLAR_DRONE,
  solarPaint: FS_SOLAR_PAINT,
  solarGrid: FS_SOLAR_GRID,
  solarRoadway: FS_SOLAR_ROADWAY,
  solarWindow: FS_SOLAR_WINDOW,
  solarHydrogen: FS_SOLAR_HYDROGEN,
  solarWearable: FS_SOLAR_WEARABLE,
  solarDeepLearning: FS_SOLAR_DEEP_LEARNING,
};

/* ------------------------------------------------------------------ */
/*  Hex → RGB helper                                                   */
/* ------------------------------------------------------------------ */
function hexToVec3(hex) {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;
  return new THREE.Vector3(r, g, b);
}

/* ------------------------------------------------------------------ */
/*  React component                                                    */
/* ------------------------------------------------------------------ */
export default function SolarPlanetRenderer({
  shader = "solarPanel",
  color = "#1a3a6b",
  accent = "#4fc3f7",
  size = 300,
  autoRotate = true,
  interactive = true,
}) {
  const mountRef = useRef(null);
  const frameRef = useRef(0);

  useEffect(() => {
    const container = mountRef.current;
    if (!container) return;

    // --- Renderer ---
    const renderer = new THREE.WebGLRenderer({
      antialias: true,
      alpha: true,
    });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(size, size);
    renderer.setClearColor(0x000000, 0);
    container.appendChild(renderer.domElement);

    // --- Scene & Camera ---
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(45, 1, 0.1, 100);
    camera.position.z = 2.8;

    // --- Geometry (icosphere approximation via high-segment UV sphere) ---
    const geometry = new THREE.SphereGeometry(1, 128, 64);

    // --- Material ---
    const fragmentShader = SHADER_MAP[shader] || FS_SOLAR_PANEL;

    const uniforms = {
      uTime: { value: 0 },
      uResolution: { value: new THREE.Vector2(size, size) },
      uColor: { value: hexToVec3(color) },
      uAccent: { value: hexToVec3(accent) },
      uLightDir: { value: new THREE.Vector3(-0.4, 0.3, 1.0).normalize() },
    };

    const material = new THREE.ShaderMaterial({
      vertexShader: VERTEX_SHADER,
      fragmentShader,
      uniforms,
      transparent: false,
    });

    const mesh = new THREE.Mesh(geometry, material);
    scene.add(mesh);

    // --- Interaction (drag to rotate) ---
    let isDragging = false;
    let prevMouse = { x: 0, y: 0 };
    let rotVelX = 0;
    let rotVelY = 0;

    const onPointerDown = (e) => {
      if (!interactive) return;
      isDragging = true;
      prevMouse = { x: e.clientX, y: e.clientY };
      container.style.cursor = "grabbing";
    };

    const onPointerMove = (e) => {
      if (!isDragging || !interactive) return;
      const dx = e.clientX - prevMouse.x;
      const dy = e.clientY - prevMouse.y;
      rotVelY += dx * 0.005;
      rotVelX += dy * 0.005;
      prevMouse = { x: e.clientX, y: e.clientY };
    };

    const onPointerUp = () => {
      isDragging = false;
      container.style.cursor = interactive ? "grab" : "default";
    };

    container.addEventListener("pointerdown", onPointerDown);
    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", onPointerUp);

    if (interactive) {
      container.style.cursor = "grab";
    }

    // --- Animation loop ---
    const clock = new THREE.Clock();

    const animate = () => {
      frameRef.current = requestAnimationFrame(animate);
      const elapsed = clock.getElapsedTime();
      uniforms.uTime.value = elapsed;

      if (autoRotate && !isDragging) {
        mesh.rotation.y += 0.003;
      }

      // Apply drag velocity with damping
      mesh.rotation.y += rotVelY;
      mesh.rotation.x += rotVelX;
      rotVelY *= 0.92;
      rotVelX *= 0.92;

      renderer.render(scene, camera);
    };

    animate();

    // --- Cleanup ---
    return () => {
      cancelAnimationFrame(frameRef.current);
      container.removeEventListener("pointerdown", onPointerDown);
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerup", onPointerUp);
      geometry.dispose();
      material.dispose();
      renderer.dispose();
      if (container.contains(renderer.domElement)) {
        container.removeChild(renderer.domElement);
      }
    };
  }, [shader, color, accent, size, autoRotate, interactive]);

  return (
    <div
      ref={mountRef}
      style={{
        width: size,
        height: size,
        borderRadius: "50%",
        overflow: "hidden",
      }}
    />
  );
}
